/**
 * CrUX Geography — per-country field data from the monthly BigQuery
 * pull (spec plans/1.1.0/crux-origins/02-bigquery.md): a choropleth
 * world map + Best / Needs-work country lists, switchable between
 * Score / LCP / INP / CLS.
 *
 * Paid-only: gated with `useEntitlement('crux_geo')` — free / suspended
 * workspaces see an inline upsell where the section would be.
 *
 * Significance: CrUX country data ships no sample counts, so "enough
 * traffic" can't be measured directly. As a proxy the Best/Needs-work
 * lists only include countries with all three core vitals present
 * (`cwv != null`) — CrUX only reports a metric once it clears a
 * per-metric sample floor, so a full core reading means meaningfully
 * more traffic than a partial one. The map still shades every covered
 * country.
 */
import { Flag } from '@/components/primitives/Flag';
import { UpsellInline } from '@/components/primitives/UpsellInline';
import { countryName } from '@/lib/country-name';
import {
  GEO_METRICS,
  type GeoMetric,
  bandQuality,
  geoBandOf,
  geoFormatValue,
  geoGoodness,
} from '@/lib/crux-geo-metric';
import { bandFill, bandText } from '@/lib/format';
import { useEntitlement } from '@/lib/queries/billing';
import { type CruxGeoCountry, useSiteCruxGeo } from '@/lib/queries/crux';
import { cn } from '@/lib/utils';
import { Globe } from '@phosphor-icons/react';
import { type ReactElement, Suspense, lazy, useState } from 'react';

// Lazy so the ~120 KB world-map geometry only downloads when a paid
// workspace actually renders the map (covered origin, Field-data tab).
const CruxWorldMap = lazy(() => import('./CruxWorldMap'));

/** Show two ranked lists once there's enough spread; a single "by
 *  country" list below that so Best and Needs-work don't overlap. */
const SPLIT_THRESHOLD = 6;
const LIST_SIZE = 5;

export function CruxGeography({
  siteUuid,
  metric,
}: {
  siteUuid: string | null;
  metric: GeoMetric;
}): ReactElement | null {
  const { allowed, isLoading: entLoading, minPlan } = useEntitlement('crux_geo');
  const geo = useSiteCruxGeo(siteUuid, { enabled: allowed && !!siteUuid });
  // The shared metric bar drives the map to a vital; this local Score
  // chip overrides it to the composite field-score-by-country view — the
  // one cut the unified bar can't carry (a composite has no good /
  // needs-work / poor split for the distribution chart to stack).
  const [showScore, setShowScore] = useState(false);

  // Avoid a flash of upsell before entitlement resolves.
  if (entLoading) return null;

  if (!allowed) {
    return (
      <section className="space-y-3">
        <SectionHeading />
        <UpsellInline
          variant="card"
          minPlan={minPlan}
          label="country-level Chrome UX Report data"
          description="See real-user Core Web Vitals broken down by country, plus popularity rank and multi-year history — from Google's public Chrome UX Report."
        />
      </section>
    );
  }

  const data = geo.data;
  // Paid but nothing to show yet (not pulled / origin not in the
  // BigQuery dataset): stay silent — the field report above already
  // explains overall CrUX coverage.
  if (!data || data.coverage !== 'ok') return null;
  const scored = data.countries.filter((c) => c.field_score != null);
  if (scored.length === 0) return null;
  const mapMetric: GeoMetric = showScore ? 'score' : metric;

  // Significance proxy: only rank countries with the full core-vitals
  // reading (see the header note).
  const significant = [...scored]
    .filter((c) => c.cwv != null)
    .sort((a, b) => geoGoodness(b, mapMetric) - geoGoodness(a, mapMetric));
  const best = significant.slice(0, LIST_SIZE);
  const worst = significant.slice(-LIST_SIZE).reverse();

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-medium text-[var(--color-ink)]">Geography</h3>
          <span className="text-[11px] text-[var(--color-mute)]">
            {scored.length} countr{scored.length === 1 ? 'y' : 'ies'} · phone ·{' '}
            {GEO_METRICS.find((m) => m.key === mapMetric)?.label}
          </span>
        </div>
        {/* Local override: the composite field-score-by-country cut the
            shared metric bar can't offer. */}
        <button
          type="button"
          aria-pressed={showScore}
          onClick={() => setShowScore((s) => !s)}
          title="Map the composite field score by country instead of the selected metric"
          className={cn(
            'rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors',
            showScore
              ? 'bg-[var(--color-ink)] text-[var(--color-paper)]'
              : 'border border-[var(--color-line)] text-[var(--color-mute)] hover:text-[var(--color-ink-soft)]',
          )}
        >
          Score
        </button>
      </div>

      <Suspense fallback={<MapSkeleton />}>
        <CruxWorldMap countries={scored} metric={mapMetric} />
      </Suspense>

      {significant.length === 0 ? (
        <p className="text-xs text-[var(--color-ink-soft)]">
          Not enough per-country traffic for a reliable ranking yet — the map above still shades
          every country Google reports.
        </p>
      ) : significant.length <= SPLIT_THRESHOLD ? (
        <CountryList title="By country" rows={significant} metric={mapMetric} />
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          <CountryList title="Best countries" rows={best} metric={mapMetric} />
          <CountryList title="Needs work" rows={worst} metric={mapMetric} />
        </div>
      )}
    </section>
  );
}

/** Reserve the map's aspect ratio while the geometry chunk loads. */
function MapSkeleton(): ReactElement {
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div
        className="mx-auto w-full max-w-[560px] animate-pulse rounded-lg bg-[var(--color-line-soft)]"
        style={{ aspectRatio: '1004 / 522' }}
      />
    </div>
  );
}

function SectionHeading(): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Globe aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
      <h3 className="text-sm font-medium text-[var(--color-ink)]">Geography</h3>
    </div>
  );
}

function CountryList({
  title,
  rows,
  metric,
}: {
  title: string;
  rows: CruxGeoCountry[];
  metric: GeoMetric;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="border-b border-[var(--color-line-soft)] px-4 py-2.5">
        <h4 className="text-xs font-semibold text-[var(--color-ink)]">{title}</h4>
      </header>
      <ul className="divide-y divide-[var(--color-line-soft)]/60">
        {rows.map((r) => {
          const band = geoBandOf(r, metric);
          // Bar wash = quality proportion, so a longer bar always reads
          // as "better" for every metric.
          const barPct = Math.max(6, Math.round(bandQuality(band) * 100));
          return (
            <li
              key={r.country_code}
              className="relative h-9"
              title={
                r.cwv ? `Core Web Vitals ${r.cwv === 'pass' ? 'passing' : 'failing'}` : undefined
              }
            >
              <div
                aria-hidden="true"
                className="absolute inset-y-0 left-0"
                style={{ width: `${barPct}%`, backgroundColor: bandFill(band), opacity: 0.16 }}
              />
              <div className="relative grid h-full items-center gap-3 px-3 [grid-template-columns:1fr_auto]">
                <div className="flex min-w-0 items-center gap-2">
                  <Flag code={r.country_code} className="h-3 w-[18px]" />
                  <span className="truncate text-xs text-[var(--color-ink-soft)]">
                    {countryName(r.country_code)}
                  </span>
                </div>
                <span className={cn('text-xs font-semibold tabular-nums', bandText(band))}>
                  {geoFormatValue(r, metric)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
